import React, { useEffect } from 'react';
import { Control, Controller, FieldErrors, useFieldArray, useWatch } from 'react-hook-form';
import { HtmlTable } from '../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { InputNumberMask } from '../../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import {
  DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
  DeferredCompensationFormValues,
} from '../DeferredCompensationForm.meta';
import { DeferredCompensationContributionUnit } from '../../../tables/DeferredCompensation/DeferredCompensation.meta';

interface Props {
  control: Control<DeferredCompensationFormValues>;
  errors: FieldErrors<DeferredCompensationFormValues>;
}

export const CustomDistributionSchedule = ({ control, errors }: Props) => {
  const { fields, update, append, remove } = useFieldArray({
    control,
    name: 'customDistributionSchedule',
  });

  const [customDistributionSchedule, distributionPeriod] = useWatch({
    name: ['customDistributionSchedule', 'distributionPeriod'],
    control,
  });

  // Sync customDistributionSchedule with distributionPeriod
  useEffect(() => {
    if (distributionPeriod === undefined) {
      return;
    }

    const currentLength = customDistributionSchedule?.length || 0;
    const expectedLength = distributionPeriod;

    // Only sync if lengths don't match
    if (currentLength !== expectedLength) {
      // Remove all existing rows
      for (let i = currentLength - 1; i >= 0; i--) {
        remove(i);
      }

      // Add new rows with 0% as default
      for (let i = 0; i < expectedLength; i++) {
        append({
          Unit: DeferredCompensationContributionUnit.Percentage,
          Value: i === expectedLength - 1 ? 100 : 0,
        });
      }
    }
  }, [distributionPeriod, append, remove]);

  return (
    <HtmlTable variant="embedded" columnSpacing="small" edgePadding={24}>
      <table style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 100 }} />
          <col style={{ width: 275 }} />
        </colgroup>

        <thead>
          <tr>
            <th>
              <Text variant="smallCaps">Year</Text>
            </th>
            <th>
              <Text variant="smallCaps" textAlign="left">
                Amount
              </Text>
            </th>
          </tr>
        </thead>

        <tbody>
          {fields.map((row, index) => (
            <tr key={row.id}>
              <td style={{ textAlign: 'center', fontWeight: 500 }}>{index + 1}</td>

              <td>
                <Controller
                  control={control}
                  name={`customDistributionSchedule.${index}.Value`}
                  render={({ field: { onChange, ...field } }) => {
                    const fieldError = errors.customDistributionSchedule?.[index]?.Value?.message;

                    const currentUnit =
                      customDistributionSchedule[index]?.Unit ??
                      DeferredCompensationContributionUnit.Percentage;
                    const selectedOption =
                      DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS.find(
                        o => o.value === currentUnit,
                      ) || DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS[1]; // Default to percentage

                    return (
                      <InputNumberMask
                        {...field}
                        key={`${index}-${currentUnit}`}
                        decimalScale={
                          currentUnit === DeferredCompensationContributionUnit.Dollar ? 2 : 2
                        }
                        allowNegative={false}
                        aria-label="Distribution Amount"
                        formVariant={fieldError ? 'error' : undefined}
                        helperText={fieldError}
                        onValueChange={({ floatValue }) => {
                          onChange(floatValue ?? null);
                        }}
                        selectorProps={{
                          defaultValue: selectedOption,
                          options: DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS,
                          onChange: option => {
                            if (option) {
                              update(index, {
                                ...customDistributionSchedule[index],
                                Unit: option.value,
                              });
                            }
                          },
                          ariaLabel: 'Distribution unit selector',
                        }}
                      />
                    );
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </HtmlTable>
  );
};
